import { useMemo, useState, type FormEvent, type JSX } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BULK_CREATE_TRIAL_VISITS_MAX_ITEMS,
  TRIAL_VISIT_CONTACTS,
  TRIAL_VISIT_KINDS,
  TRIAL_VISIT_REFERENCE_TYPES,
  TRIAL_VISIT_TYPES,
  type BulkCreateTrialVisitsRequest,
  type TrialVisitDto,
  type TrialVisitGroupDto,
} from '@ctms/contracts';
import { isHTTPError } from 'ky';
import { AlertCircle, X } from 'lucide-react';
import { toast } from 'sonner';

import { getApiErrorMessage } from '../../../api/client.js';
import { trialsApi } from '../trials.api.js';
import { trialQueryKeys } from '../trials.query-keys.js';

/**
 * „++" wizard (D2, #40): bulk creation of catalog visits in ONE server
 * transaction. Step 1 generates Nazwa 1 from a stem + counter pattern with a
 * live preview; step 2 optionally stamps Nazwa 2 onto CHECKED rows only
 * (druga nazwa zwykle wymaga kilku przebiegów — Zastosuj można kliknąć wiele
 * razy z różnymi wzorcami). `visitNumber` is ALWAYS server-assigned (REQ-008)
 * — the preview "Numer" column is only the 1-based batch ordinal.
 */

export interface BulkCreateVisitsDialogProps {
  trialId: number;
  /** Fired after a successful bulk — the parent invalidates queries and closes the dialog. */
  onCreated: (created: TrialVisitDto[]) => void;
  onClose: () => void;
}

type NamePosition = 'prefix' | 'suffix';

export interface NamePattern {
  stem: string;
  position: NamePosition;
  space: boolean;
  start: string;
  interval: string;
}

interface BulkRow {
  /** 1-based position in the generated batch — the preview "Numer" column. */
  ordinal: number;
  name1: string;
  name2: string | null;
  selected: boolean;
}

export const DEFAULT_NAME_PATTERN: NamePattern = {
  stem: '',
  position: 'suffix',
  space: true,
  start: '1',
  interval: '1',
};

const fieldClass =
  'mt-1.5 w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 placeholder-slate-500 focus:border-cyan-500 focus:outline-none disabled:opacity-50';

const labelClass = 'block text-xs font-medium uppercase tracking-wider text-slate-300';

/** Integers only (start may be negative, e.g. -7 for screening windows). */
export function parseInteger(raw: string): number | undefined {
  const trimmed = raw.trim();
  if (!/^-?\d+$/.test(trimmed)) {
    return undefined;
  }
  const parsed = Number(trimmed);
  return Number.isSafeInteger(parsed) ? parsed : undefined;
}

/** `suffix: stem␣value` / `prefix: value␣stem`; the space is optional; result trimmed. */
export function composeName(pattern: NamePattern, value: number): string {
  const separator = pattern.space ? ' ' : '';
  const stem = pattern.stem.trim();
  const composed =
    pattern.position === 'suffix' ? `${stem}${separator}${value}` : `${value}${separator}${stem}`;
  return composed.trim();
}

/** value_i = start + (i - 1) * interval, i = 1..count; null when the pattern is invalid. */
export function generatedNames(count: number, pattern: NamePattern): string[] | null {
  const start = parseInteger(pattern.start);
  const interval = parseInteger(pattern.interval);
  if (start === undefined || interval === undefined || interval < 1) {
    return null;
  }
  return Array.from({ length: count }, (_, index) => composeName(pattern, start + index * interval));
}

function countError(raw: string): string | null {
  const count = parseInteger(raw);
  if (count === undefined || count < 1 || count > BULK_CREATE_TRIAL_VISITS_MAX_ITEMS) {
    return `Liczba wizyt musi być liczbą całkowitą od 1 do ${BULK_CREATE_TRIAL_VISITS_MAX_ITEMS}.`;
  }
  return null;
}

function patternError(pattern: NamePattern): string | null {
  if (parseInteger(pattern.start) === undefined) {
    return 'Pole „Licz od" musi być liczbą całkowitą.';
  }
  const interval = parseInteger(pattern.interval);
  if (interval === undefined || interval < 1) {
    return 'Pole „Interwał" musi być liczbą całkowitą większą od zera.';
  }
  return null;
}

/** 1 wizytę / 2–4 wizyty / 5+ wizyt (Polish plural rules). */
export function pluralizeVisits(count: number): string {
  if (count === 1) {
    return 'wizytę';
  }
  const rest10 = count % 10;
  const rest100 = count % 100;
  if (rest10 >= 2 && rest10 <= 4 && (rest100 < 12 || rest100 > 14)) {
    return 'wizyty';
  }
  return 'wizyt';
}

function PatternFields({
  idPrefix,
  title,
  pattern,
  disabled,
  onChange,
}: {
  idPrefix: string;
  title: string;
  pattern: NamePattern;
  disabled: boolean;
  onChange: (next: NamePattern) => void;
}): JSX.Element {
  return (
    <fieldset disabled={disabled} className="space-y-3 rounded-md border border-slate-800 p-3">
      <legend className={labelClass}>{title}</legend>
      <div className="flex items-center gap-4">
        <label className="flex items-center gap-1.5 text-sm text-slate-200">
          <input
            id={`${idPrefix}-suffix`}
            name={`${idPrefix}-position`}
            type="radio"
            checked={pattern.position === 'suffix'}
            onChange={() => onChange({ ...pattern, position: 'suffix' })}
          />
          Suffix
        </label>
        <label className="flex items-center gap-1.5 text-sm text-slate-200">
          <input
            id={`${idPrefix}-prefix`}
            name={`${idPrefix}-position`}
            type="radio"
            checked={pattern.position === 'prefix'}
            onChange={() => onChange({ ...pattern, position: 'prefix' })}
          />
          Prefix
        </label>
      </div>
      <div>
        <label htmlFor={`${idPrefix}-stem`} className={labelClass}>Nazwa</label>
        <input
          id={`${idPrefix}-stem`}
          type="text"
          className={fieldClass}
          value={pattern.stem}
          onChange={(event) => onChange({ ...pattern, stem: event.target.value })}
        />
      </div>
      <label className="flex items-center gap-2 text-sm text-slate-200">
        <input
          id={`${idPrefix}-space`}
          type="checkbox"
          checked={pattern.space}
          onChange={(event) => onChange({ ...pattern, space: event.target.checked })}
        />
        Spacja
      </label>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label htmlFor={`${idPrefix}-start`} className={labelClass}>Licz od</label>
          <input
            id={`${idPrefix}-start`}
            type="text"
            inputMode="numeric"
            className={fieldClass}
            value={pattern.start}
            onChange={(event) => onChange({ ...pattern, start: event.target.value })}
          />
        </div>
        <div>
          <label htmlFor={`${idPrefix}-interval`} className={labelClass}>Interwał</label>
          <input
            id={`${idPrefix}-interval`}
            type="text"
            inputMode="numeric"
            className={fieldClass}
            value={pattern.interval}
            onChange={(event) => onChange({ ...pattern, interval: event.target.value })}
          />
        </div>
      </div>
    </fieldset>
  );
}

export function BulkCreateVisitsDialog({
  trialId,
  onCreated,
  onClose,
}: BulkCreateVisitsDialogProps): JSX.Element {
  const [step, setStep] = useState<1 | 2>(1);
  const [count, setCount] = useState('4');
  const [groupId, setGroupId] = useState('');
  const [pattern1, setPattern1] = useState<NamePattern>(DEFAULT_NAME_PATTERN);
  const [pattern2, setPattern2] = useState<NamePattern>(DEFAULT_NAME_PATTERN);
  const [rows, setRows] = useState<BulkRow[]>([]);
  const [formError, setFormError] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [isCreatingGroup, setIsCreatingGroup] = useState(false);
  const [cancelArmed, setCancelArmed] = useState(false);
  const queryClient = useQueryClient();

  // Until A4b ships the group manager this list only holds groups created via API.
  const groupsQuery = useQuery({
    queryKey: trialQueryKeys.visitGroups(trialId),
    queryFn: () => trialsApi.listVisitGroups(trialId),
    staleTime: 5 * 60 * 1000,
    retry: (retryCount, err) => (isHTTPError(err) ? false : retryCount < 3),
  });
  const groups = groupsQuery.data ?? [];
  const selectedGroupName = useMemo(() => {
    if (groupId === '') {
      return 'Bez grupy';
    }
    if (groupId === '__new__') {
      return newGroupName.trim() === '' ? 'Nowa grupa…' : newGroupName.trim();
    }
    return groups.find((group) => String(group.id) === groupId)?.name ?? 'Bez grupy';
  }, [groups, groupId, newGroupName]);

  const previewNames = useMemo(() => {
    const parsedCount = parseInteger(count);
    if (
      parsedCount === undefined ||
      parsedCount < 1 ||
      parsedCount > BULK_CREATE_TRIAL_VISITS_MAX_ITEMS
    ) {
      return null;
    }
    return generatedNames(parsedCount, pattern1);
  }, [count, pattern1]);

  const goToStep2 = (): void => {
    const validationError = countError(count) ?? patternError(pattern1);
    if (validationError !== null) {
      setFormError(validationError);
      return;
    }
    if (pattern1.stem.trim() === '') {
      setFormError('Pole „Nazwa” we wzorcu nazwy 1 nie może być puste.');
      return;
    }
    if (groupId === '__new__') {
      setFormError('Utwórz nową grupę albo wybierz inną opcję grupy.');
      return;
    }
    setFormError(null);
    const names = generatedNames(parseInteger(count) as number, pattern1);
    // Rows are re-materialized on every entry — a back-and-forth drops stamped
    // Nazwa 2 values (v1 trade-off, the preview on step 1 stays the source).
    setRows(
      (names ?? []).map((name1, index) => ({
        ordinal: index + 1,
        name1,
        name2: null,
        selected: true,
      })),
    );
    setStep(2);
  };

  const applyName2 = (): void => {
    const validationError = patternError(pattern2);
    if (validationError !== null) {
      setFormError(validationError);
      return;
    }
    setFormError(null);
    const start = parseInteger(pattern2.start) as number;
    const interval = parseInteger(pattern2.interval) as number;
    setRows((current) =>
      current.map((row) =>
        row.selected
          ? { ...row, name2: composeName(pattern2, start + (row.ordinal - 1) * interval) }
          : row,
      ),
    );
  };

  const clearStep2Form = (): void => {
    setPattern2(DEFAULT_NAME_PATTERN);
    setFormError(null);
    setRows((current) => current.map((row) => ({ ...row, selected: false })));
  };

  /** #44 W1: inline group creation — the cache is patched so the picker sees it at once. */
  const handleCreateGroup = async (): Promise<void> => {
    const name = newGroupName.trim();
    if (name === '') {
      setFormError('Nazwa nowej grupy nie może być pusta.');
      return;
    }
    setFormError(null);
    setIsCreatingGroup(true);
    try {
      const created = await trialsApi.createVisitGroup(trialId, { name });
      queryClient.setQueryData<TrialVisitGroupDto[]>(
        trialQueryKeys.visitGroups(trialId),
        (current) => [...(current ?? []), created],
      );
      setGroupId(String(created.id));
      setNewGroupName('');
    } catch (err: unknown) {
      setErrorMessage(await getApiErrorMessage(err, 'Nie udało się utworzyć grupy.'));
    } finally {
      setIsCreatingGroup(false);
    }
  };

  const toggleRow = (ordinal: number): void => {
    setRows((current) =>
      current.map((row) => (row.ordinal === ordinal ? { ...row, selected: !row.selected } : row)),
    );
  };

  const toggleAllRows = (): void => {
    setRows((current) => {
      const allSelected = current.every((row) => row.selected);
      return current.map((row) => ({ ...row, selected: !allSelected }));
    });
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (groupId === '__new__') {
      setFormError('Utwórz nową grupę albo wybierz inną opcję grupy.');
      return;
    }
    setErrorMessage(null);
    setIsSubmitting(true);
    try {
      const parsedGroupId = groupId === '' ? undefined : Number(groupId);
      const request: BulkCreateTrialVisitsRequest = {
        items: rows.map((row) => ({
          visitName1: row.name1,
          ...(row.name2 !== null ? { visitName2: row.name2 } : {}),
          // Decyzja #40-A4 (4): the v1 wizard does not collect kind/type —
          // every bulk row is a regular site visit; edit fixes single rows.
          kind: TRIAL_VISIT_KINDS.REGULAR,
          window: false,
          type: TRIAL_VISIT_TYPES.S,
          contactType: TRIAL_VISIT_CONTACTS.SITE,
          referenceType: TRIAL_VISIT_REFERENCE_TYPES.VISIT,
          ...(parsedGroupId !== undefined ? { groupId: parsedGroupId } : {}),
        })),
      };
      const response = await trialsApi.bulkCreateVisits(trialId, request);
      toast.success(
        `Utworzono ${response.created.length} ${pluralizeVisits(response.created.length)}`,
      );
      onCreated(response.created);
    } catch (err: unknown) {
      setErrorMessage(await getApiErrorMessage(err, 'Nie udało się utworzyć wizyt.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const notice = formError ?? errorMessage;
  const bannerText =
    step === 1 ? 'KROK 1 — Ustaw ilość wizyt i pierwszą nazwę' : 'KROK 2 — Ustaw drugą nazwę';

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="bulk-visits-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 p-4"
    >
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-xl border border-slate-800 bg-slate-900 p-6 text-slate-100 shadow-2xl">
        <div className="flex items-start justify-between gap-3 border-b border-slate-800 pb-4">
          <div>
            <h2 id="bulk-visits-title" className="text-base font-semibold text-slate-100">
              Dodaj wiele wizyt jednocześnie
            </h2>
            <p className="mt-1 text-xs text-slate-400">
              Numeracja wizyt nadawana jest automatycznie przez serwer.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            aria-label="Zamknij"
            className="rounded-md p-1.5 text-slate-400"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div
          className="mt-4 rounded-md border border-emerald-700 bg-emerald-950/60 px-3 py-2 text-xs font-medium uppercase tracking-wider text-emerald-200"
          data-testid="bulk-step-banner"
        >
          {bannerText}
        </div>

        {notice !== null && (
          <div
            role="alert"
            className="mt-4 flex items-center gap-2 rounded-md border border-rose-800/60 bg-rose-950/40 p-3 text-sm text-rose-300"
          >
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>{notice}</span>
          </div>
        )}

        <form
          onSubmit={(event) => {
            if (step === 2) {
              void handleSubmit(event);
            }
          }}
          className="mt-5 space-y-4"
          noValidate
        >
          {step === 1 && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor="bulk-count" className={labelClass}>Liczba wizyt</label>
                  <input
                    id="bulk-count"
                    type="text"
                    inputMode="numeric"
                    disabled={isSubmitting}
                    className={fieldClass}
                    value={count}
                    onChange={(event) => setCount(event.target.value)}
                  />
                </div>
                <div>
                  <label htmlFor="bulk-group" className={labelClass}>Grupa</label>
                  <select
                    id="bulk-group"
                    disabled={isSubmitting || isCreatingGroup}
                    className={fieldClass}
                    value={groupId}
                    onChange={(event) => setGroupId(event.target.value)}
                  >
                    <option value="">Bez grupy</option>
                    {groups.map((group) => (
                      <option key={group.id} value={String(group.id)}>{group.name}</option>
                    ))}
                    <option value="__new__">+ Nowa grupa…</option>
                  </select>
                </div>
              </div>

              {groupId === '__new__' && (
                <div className="flex items-end gap-2">
                  <div className="flex-1">
                    <label htmlFor="bulk-new-group-name" className={labelClass}>
                      Nazwa nowej grupy
                    </label>
                    <input
                      id="bulk-new-group-name"
                      type="text"
                      disabled={isSubmitting || isCreatingGroup}
                      className={fieldClass}
                      value={newGroupName}
                      onChange={(event) => setNewGroupName(event.target.value)}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => void handleCreateGroup()}
                    disabled={isSubmitting || isCreatingGroup}
                    className="rounded-md bg-cyan-600 px-3.5 py-2 text-xs font-medium text-white hover:bg-cyan-500 disabled:opacity-50"
                  >
                    {isCreatingGroup ? 'Tworzenie…' : 'UTWÓRZ GRUPĘ'}
                  </button>
                </div>
              )}

              <PatternFields
                idPrefix="name1"
                title="Wzorzec nazwy 1"
                pattern={pattern1}
                disabled={isSubmitting}
                onChange={setPattern1}
              />

              <div>
                <h3 className={labelClass}>Podgląd</h3>
                <div className="mt-1.5 overflow-hidden rounded-lg border border-slate-800">
                  <table className="w-full text-left text-xs">
                    <thead>
                      <tr className="border-b border-slate-800 bg-slate-950/60 text-slate-400">
                        <th scope="col" className="px-3 py-2">Numer</th>
                        <th scope="col" className="px-3 py-2">Nazwa 1</th>
                        <th scope="col" className="px-3 py-2">Grupa</th>
                      </tr>
                    </thead>
                    <tbody data-testid="bulk-preview-body">
                      {previewNames === null ? (
                        <tr>
                          <td colSpan={3} className="px-3 py-2 text-slate-500">
                            Popraw liczbę wizyt i wzorzec, aby zobaczyć podgląd nazw.
                          </td>
                        </tr>
                      ) : (
                        previewNames.map((name, index) => (
                          <tr key={`${index}-${name}`} className="border-b border-slate-800/80">
                            <td className="px-3 py-2 text-slate-400">{index + 1}</td>
                            <td className="px-3 py-2 text-slate-200">{name}</td>
                            <td className="px-3 py-2 text-slate-400">{selectedGroupName}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <PatternFields
                idPrefix="name2"
                title="Wzorzec nazwy 2"
                pattern={pattern2}
                disabled={isSubmitting}
                onChange={setPattern2}
              />

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={applyName2}
                  disabled={isSubmitting}
                  className="rounded-md bg-cyan-600 px-3.5 py-1.5 text-xs font-medium text-white hover:bg-cyan-500 disabled:opacity-50"
                >
                  ZASTOSUJ
                </button>
                <button
                  type="button"
                  onClick={clearStep2Form}
                  disabled={isSubmitting}
                  className="rounded-md border border-slate-700 px-3 py-1.5 text-xs text-slate-300 hover:bg-slate-800 disabled:opacity-50"
                >
                  WYCZYŚĆ FORMULARZ
                </button>
              </div>

              <div className="overflow-hidden rounded-lg border border-slate-800">
                <table className="w-full text-left text-xs">
                  <thead>
                    <tr className="border-b border-slate-800 bg-slate-950/60 text-slate-400">
                      <th scope="col" className="px-3 py-2">
                        <input
                          type="checkbox"
                          aria-label="Zaznacz wszystkie wiersze"
                          checked={rows.length > 0 && rows.every((row) => row.selected)}
                          onChange={toggleAllRows}
                          disabled={isSubmitting}
                        />
                      </th>
                      <th scope="col" className="px-3 py-2">Numer</th>
                      <th scope="col" className="px-3 py-2">Nazwa 1</th>
                      <th scope="col" className="px-3 py-2">Nazwa 2</th>
                      <th scope="col" className="px-3 py-2">Grupa</th>
                    </tr>
                  </thead>
                  <tbody data-testid="bulk-rows-body">
                    {rows.map((row) => (
                      <tr key={row.ordinal} className="border-b border-slate-800/80">
                        <td className="px-3 py-2">
                          <input
                            type="checkbox"
                            aria-label={`Zaznacz wiersz ${row.ordinal}`}
                            checked={row.selected}
                            onChange={() => toggleRow(row.ordinal)}
                            disabled={isSubmitting}
                          />
                        </td>
                        <td className="px-3 py-2 text-slate-400">{row.ordinal}</td>
                        <td className="px-3 py-2 text-slate-200">{row.name1}</td>
                        <td className="px-3 py-2 text-slate-200">{row.name2 ?? '—'}</td>
                        <td className="px-3 py-2 text-slate-400">{selectedGroupName}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          <div className="flex justify-end gap-2 border-t border-slate-800 pt-4">
            <button
              type="button"
              onClick={() => setStep(1)}
              disabled={step === 1 || isSubmitting}
              className="rounded-md border border-slate-700 px-3 py-1.5 text-xs text-slate-300 hover:bg-slate-800 disabled:opacity-50"
            >
              PREV STEP
            </button>
            {step === 1 ? (
              <button
                type="button"
                onClick={goToStep2}
                className="rounded-md bg-cyan-600 px-3.5 py-1.5 text-xs font-medium text-white hover:bg-cyan-500"
              >
                NEXT STEP
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => {
                    if (cancelArmed) {
                      onClose();
                    } else {
                      setCancelArmed(true);
                    }
                  }}
                  disabled={isSubmitting}
                  className="rounded-md border border-slate-700 px-3 py-1.5 text-xs text-slate-300 hover:bg-slate-800"
                >
                  {cancelArmed ? 'TAK, ANULUJ' : 'Anuluj'}
                </button>
                {cancelArmed && (
                  <>
                    <span className="self-center text-xs text-rose-300">
                      Utracisz wprowadzone dane w tym oknie.
                    </span>
                    <button
                      type="button"
                      onClick={() => setCancelArmed(false)}
                      disabled={isSubmitting}
                      className="rounded-md border border-slate-700 px-3 py-1.5 text-xs text-slate-300 hover:bg-slate-800"
                    >
                      WRÓĆ
                    </button>
                  </>
                )}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="rounded-md bg-cyan-600 px-3.5 py-1.5 text-xs font-medium text-white hover:bg-cyan-500 disabled:opacity-50"
                >
                  {isSubmitting ? 'Tworzenie…' : 'UTWÓRZ WIZYTY'}
                </button>
              </>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
